import { useMemo } from 'react';

import { IFormOption } from '../_core/_ui/forms.component';
import { getAuthUser } from '../../lib/services/auth.service';
import { Sport } from '../../lib/types';
import { useCompanySport } from '../../lib/stores/company.store';
import { buildStagesFromBlueprints } from '../../lib/helpers/format-template.helpers';
import { useStageReferenceData } from '../AssessmentStage/use-stage-reference-data';

interface IFormatTemplateReferenceDataResult {
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  positionOptions: IFormOption[];
  loading: boolean;
}

interface IUseFormatTemplateStagesResult extends IFormatTemplateReferenceDataResult {
  stages: IAssessmentStage[];
}

// Reference data (drills / practice plans / positions) a template's stages reference. Positions
// resolve against the template's own sport so a SYSTEM template's globals render for any viewer.
// Pass globalOnly ONLY from a pure picker: these options also rehydrate stored ids via
// buildOptions, which drops unmatched ones — a filtered list would erase them on save.
export const useFormatTemplateReferenceData = (
  template: IFormatTemplate | null,
  open: boolean,
  globalOnly = false,
): IFormatTemplateReferenceDataResult => {
  const user = getAuthUser();
  const companySport = useCompanySport();

  return useStageReferenceData({
    companyId: user?.settings.activeCompanyId,
    sport: (template?.sport as Sport) || companySport,
    includeGlobal: user?.settings.includeGlobal,
    globalOnly,
    skip: !open,
  });
};

// Rehydrate a stored template's blueprints into read-only stages alongside that reference data.
// CATALOG templates hold no blueprints — their stages come from formatTemplatePreview instead.
export const useFormatTemplateStages = (
  template: IFormatTemplate | null,
  open: boolean,
): IUseFormatTemplateStagesResult => {
  const refData = useFormatTemplateReferenceData(template, open);

  const stages = useMemo(
    () => buildStagesFromBlueprints(template?.stageBlueprints ?? []),
    [template],
  );

  return { stages, ...refData };
};
